import { useEffect, useState } from 'react';
import { useApi } from '../contexts/ApiContext';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, Filter, Search, X, KeyRound, Bot, ScrollText } from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';

interface AuditLogEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  changes: string | null;
  api_key_id: string | null;
  user_id: string | null;
  username: string | null;
  display_name: string | null;
  /** Name of the API key that made the change (null if a session, or the key was deleted). */
  api_key_name: string | null;
  api_key_created_by: string | null;
  /** 1 when no user or key made the change (e.g. the NS change tracker). */
  is_system: number;
  /** For system rows: who requested the change the system acted on. */
  on_behalf_of: string | null;
  created_at: string;
}

interface Filters {
  entity_types: string[];
  actions: string[];
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  sync: 'bg-purple-100 text-purple-700',
  import: 'bg-amber-100 text-amber-700',
  nameservers: 'bg-indigo-100 text-indigo-700',
  bulk_nameservers: 'bg-indigo-100 text-indigo-700',
};

export default function AuditLogPage() {
  const { fetchApi } = useApi();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<Filters>({ entity_types: [], actions: [] });
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const limit = 50;

  useEffect(() => {
    fetchApi<{ success: boolean; data: Filters }>('/audit-logs/filters')
      .then((res) => { if (res.data) setFilters(res.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, entityType, action, search]);

  async function loadLogs() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (entityType) params.set('entity_type', entityType);
      if (action) params.set('action', action);
      if (search) params.set('search', search);

      const res = await fetchApi<{
        success: boolean;
        data: AuditLogEntry[];
        meta: { total: number; pages: number };
      }>(`/audit-logs?${params}`);

      if (res.data) setLogs(res.data);
      if (res.meta) {
        setTotalPages(res.meta.pages);
        setTotal(res.meta.total);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  function clearFilters() {
    setEntityType('');
    setAction('');
    setSearch('');
    setSearchInput('');
    setPage(1);
  }

  function formatChanges(changes: string | null): React.ReactNode {
    if (!changes) return <span className="text-slate-400">-</span>;
    try {
      const parsed = JSON.parse(changes);
      return (
        <pre className="text-xs bg-slate-50 rounded-lg p-3 overflow-x-auto max-h-64 whitespace-pre-wrap">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      );
    } catch {
      return <span className="text-xs text-slate-500">{changes}</span>;
    }
  }

  const hasFilters = entityType || action || search;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        description={`Track all changes made across the system (${total.toLocaleString()} entries)`}
      />

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-slate-400" />

          <select
            value={entityType}
            onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
            className="input w-auto text-sm py-1.5"
          >
            <option value="">All entities</option>
            {filters.entity_types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <select
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
            className="input w-auto text-sm py-1.5"
          >
            <option value="">All actions</option>
            {filters.actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>

          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search changes..."
                className="input text-sm py-1.5 pl-8 w-48"
              />
            </div>
            <button type="submit" className="btn btn-primary text-sm py-1.5 px-3">
              Search
            </button>
          </form>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="text-left font-medium text-slate-500 px-4 py-3">Time</th>
                <th className="text-left font-medium text-slate-500 px-4 py-3">User</th>
                <th className="text-left font-medium text-slate-500 px-4 py-3">Action</th>
                <th className="text-left font-medium text-slate-500 px-4 py-3">Entity</th>
                <th className="text-left font-medium text-slate-500 px-4 py-3">Entity ID</th>
                <th className="text-left font-medium text-slate-500 px-4 py-3">Changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <SkeletonRows rows={8} cols={6} />
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      compact
                      icon={ScrollText}
                      title="No audit log entries found"
                      description={hasFilters ? 'Try clearing the filters.' : undefined}
                    />
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                  >
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                      {format(new Date(log.created_at + 'Z'), 'MMM d, yyyy HH:mm:ss')}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      <ActorCell log={log} />
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{log.entity_type}</td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono text-xs max-w-[200px] truncate">
                      {log.entity_id}
                    </td>
                    <td className="px-4 py-2.5">
                      {expandedRow === log.id ? (
                        formatChanges(log.changes)
                      ) : (
                        <span className="text-slate-400 text-xs">
                          {log.changes ? 'Click to expand' : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <span className="text-sm text-slate-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="btn text-sm py-1.5 px-2 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="btn text-sm py-1.5 px-2 disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Who made the change: a user (session), an API key (by name, with its
 * creator in the tooltip), or the system acting on someone's request (the
 * NS change tracker confirming a nameserver change).
 */
function ActorCell({ log }: { log: AuditLogEntry }) {
  if (log.user_id) {
    return <>{log.display_name || log.username || 'Deleted user'}</>;
  }
  if (log.api_key_id) {
    return (
      <span
        className="inline-flex items-center gap-1.5"
        title={log.api_key_name
          ? `API key "${log.api_key_name}"${log.api_key_created_by ? `, created by ${log.api_key_created_by}` : ''}`
          : `API key ${log.api_key_id} (deleted)`}
      >
        <KeyRound className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        {log.api_key_name ?? <span className="text-slate-400">Deleted API key</span>}
      </span>
    );
  }
  if (log.is_system) {
    return (
      <span className="inline-flex flex-col leading-tight" title="Recorded automatically by a background check">
        <span className="inline-flex items-center gap-1.5 text-slate-500">
          <Bot className="h-3.5 w-3.5 shrink-0" /> System
        </span>
        {log.on_behalf_of && <span className="text-xs text-slate-400">for {log.on_behalf_of}</span>}
      </span>
    );
  }
  return <>-</>;
}
